import { useMemo, useCallback } from "react";
import { useAuth } from "../../../../AuthContext";
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React icons
import SpaceShip from "examples/Icons/SpaceShip";
import Document from "examples/Icons/Document";
import Cube from "examples/Icons/Cube";
import Shop from "examples/Icons/Shop";
import Office from "examples/Icons/Office";
import CustomerSupport from "examples/Icons/CustomerSupport";
import Settings from "examples/Icons/Settings";
import PropTypes from "prop-types";

function Sidenav({ basicInfo }) {

  const { permissions: ctxPerms = [] } = useAuth();

  const storedPerms = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("permissions") || "[]");
    } catch {
      return (localStorage.getItem("permissions") || "")
        .split(",")
        .filter(Boolean);
    }
  }, []);

  const permSet = useMemo(() => {
    const perms = ctxPerms.length ? ctxPerms : storedPerms;
    return new Set(perms.map((p) => (typeof p === "string" ? p : p.name)));
  }, [ctxPerms, storedPerms]);

  const hasSectionPermission = useCallback(
    (sectionName) => permSet.has(`InterviewSelected.${sectionName}`),
    [permSet]
  );
  // const sidenavItems = [
  //   { icon: <Document />, label: "basic info", href: "basic-info" },
  //   { icon: <Shop />, label: "Educational details", href: "educational-details" },
  //   { icon: <Settings />, label: "uploadDocuments", href: "uploadDocuments" },
  //   { icon: <CustomerSupport />, label: "salary structure", href: "salary-structure" },
  //   { icon: <Cube />, label: "job related data", href: "job-related-data" },
  //   { icon: <Document />, label: "Account Details", href: "account-details" },
  //   { icon: <Document />, label: "change user password", href: "change-user-password" },
  // ];

  const sidenavItems = [
    { icon: <Document />, label: "basic info", href: "basic-info", permission: "BasicInfo" },
    { icon: <Shop />, label: "Educational details", href: "educational-details", permission: "EducationalDetails" },
    { icon: <Settings />, label: "uploadDocuments", href: "uploadDocuments", permission: "UploadDocuments" },
    { icon: <CustomerSupport />, label: "salary structure", href: "salary-structure", permission: "SalaryStructure" },
    { icon: <Cube />, label: "job related data", href: "job-related-data", permission: "JobRelatedData" },
    { icon: <Document />, label: "Account Details", href: "account-details", permission: "AccountDetails" },
    { icon: <Document />, label: "change user password", href: "change-user-password", permission: "ChangePassword" },
  ];

  // const renderSidenavItems = sidenavItems.map(({ icon, label, href }, key) => {
  const renderSidenavItems = sidenavItems
    .filter((item) => hasSectionPermission(item.permission))
    .map(({ icon, label, href }, key) => {
      const itemKey = `item-${key}`;
      return (
        <SoftBox key={itemKey} component="li" pt={1}>
          <SoftTypography
            component="a"
            href={`#${href}`}
            variant="button"
            fontWeight="regular"
            color="text"
            textTransform="capitalize"
            sx={({
              borders: { borderRadius },
              functions: { pxToRem },
              palette: { light },
              transitions,
            }) => ({
              display: "flex",
              alignItems: "center",
              borderRadius: borderRadius.md,
              padding: `${pxToRem(10)} ${pxToRem(16)}`,
              transition: transitions.create("background-color", {
                easing: transitions.easing.easeInOut,
                duration: transitions.duration.shorter,
              }),

              "&:hover": {
                backgroundColor: light.main,
              },
            })}
          >
            <SoftBox mr={1.5} lineHeight={1}>
              {icon}
            </SoftBox>
            {label}
          </SoftTypography>
        </SoftBox>
      );
    });

  return (
    <Card
      sx={{
        borderRadius: ({ borders: { borderRadius } }) => borderRadius.lg,
        position: "sticky",
        top: "1%",
      }}
    >
      <SoftBox
        component="ul"
        display="flex"
        flexDirection="column"
        p={2}
        m={0}
        sx={{ listStyle: "none" }}
      >
        {/* Profile Section */}
        <SoftBox component="li" pb={2} sx={{ borderBottom: "1px solid #f0f2f5" }}>
          <SoftBox
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            {/* Dummy Profile Image */}
            <SoftBox
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "#e0e0e0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 1.5,
                overflow: "hidden",
              }}
            >
              {basicInfo?.photo ? (
                <img
                  src={basicInfo.photo}
                  alt={basicInfo?.name || "Profile"}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <SpaceShip size="2rem" />
              )}
            </SoftBox>

            {/* Profile Name */}
            <SoftTypography variant="h6" fontWeight="medium" gutterBottom>
              {basicInfo?.name}
            </SoftTypography>

            {/* Profile Email */}
            <SoftTypography variant="caption" color="text" fontWeight="regular">
              {basicInfo?.email}
            </SoftTypography>
          </SoftBox>
        </SoftBox>

        {/* Navigation Items */}
        {renderSidenavItems}
      </SoftBox>
    </Card>
  );
}

export default Sidenav;

Sidenav.propTypes = {
  basicInfo: PropTypes.object,
};
